import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { arrayMove } from '@dnd-kit/sortable'
import type { ColumnDef, Row, SortingState } from '@tanstack/react-table'
import {
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChevronDown } from 'lucide-react'
import { useLocation } from 'react-router-dom'

import type { AssetTableEntryDTO } from '@/api/repository/repositorySchemas'
import AstaLoader from '@/components/shared/AstaLoader/AstaLoader'
import { Button } from '@/components/ui/button'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useSelectedRowsContext } from '@/pages/App/Assets/context/SelectedRowsContext'

import { TableBody as TB } from './TableBody'
import { TableHeaders } from './TableHeaders'

export const ExpanderCell: React.FC<{ children; row; depth }> = React.memo(
	({ children, row, depth }) => {
		const marginLeftStyle = React.useMemo(
			() => ({
				marginLeft: `${depth * 28 + (row.getCanExpand() ? 0 : 18)}px`,
			}),
			[depth, row]
		)
		return (
			<div className="flex flex-row items-center h-full">
				<div className="mr-2" style={marginLeftStyle}>
					{row.getCanExpand() && (
						<Button
							onClick={row.getToggleExpandedHandler()}
							className="p-0 w-5 h-5 cursor-pointer"
							variant="ghost"
						>
							<ChevronDown
								className={
									row.getIsExpanded()
										? ''
										: 'transform -rotate-90 transition-transform'
								}
							/>
						</Button>
					)}
				</div>
				{children}
			</div>
		)
	}
)
ExpanderCell.displayName = 'ExpanderCell'

const EmptyTableView = () => {
	return (
		<div className="flex flex-col items-center justify-center text-center space-y-4 w-full h-[calc(100vh-7.5rem)] absolute top-[117px]">
			<h1 className="text-4xl">Table is empty.</h1>
			<p className="text-xl">
				There are no resources available for this table.
			</p>
		</div>
	)
}

interface TableProps<T> {
	data: T[]
	columns: ColumnDef<T>[]
	actions: React.ReactNode[]
	rowOnClick?: (data: T) => void
	fetchNextPage?: any
	hasNextPage?: boolean
	isFetching?: boolean
	contextMenuOptions?: {
		label: string
		onClick: (rows: any[]) => void
	}[]
	columnVisibility?: Record<string, boolean>
	setColumnVisibility?: React.Dispatch<React.SetStateAction<{}>>
	columnOrder?: string[]
	setColumnOrder?: (columnOrder: string[]) => void
	selectedRowId?: string | number
	isLoading?: boolean
	textFilter?: string
	areRowsExpanded?: boolean
	sort?: string
	setSort?: (value: string) => void
	containerRef?: React.MutableRefObject<HTMLDivElement>
}

interface DataWithSubRows {
	subRows?: any[]
	[key: string]: any
}

export function AstaTableV2<T extends DataWithSubRows>({
	data,
	columns,
	rowOnClick,
	isFetching = false,
	hasNextPage: _hasNextPage = false,
	fetchNextPage: _fetchNextPage,
	contextMenuOptions,
	columnVisibility,
	setColumnVisibility,
	columnOrder: _columnOrder,
	setColumnOrder,
	selectedRowId,
	isLoading = false,
	textFilter,
	areRowsExpanded,
	sort,
	setSort,
	containerRef,
}: TableProps<T>): JSX.Element {
	const [expanded, setExpanded] = React.useState<
		true | Record<string, boolean>
	>(areRowsExpanded || {})

	useEffect(() => {
		setExpanded(areRowsExpanded || {})
	}, [areRowsExpanded])

	const [rowSelection, setRowSelection] = useState({})
	const [sorting, setSorting] = useState<SortingState>([])
	const tableParentRef = React.useRef<HTMLDivElement>(null)

	const { setSelectedRows } = useSelectedRowsContext()

	const { pathname } = useLocation()
	const LSKey = pathname.split('/').at(-1) || ''

	//Using LS to persist column order for tables that don't use views
	const [internalColumnOrder, setInternalColumnOrder] = useLocalStorage<
		string[]
	>(`columnOrder-${LSKey}`, () => columns.map(c => c.id!))

	const columnOrder = useMemo(
		() => (_columnOrder?.length > 0 ? _columnOrder : internalColumnOrder),
		[_columnOrder, internalColumnOrder]
	)

	const setColSorting = useCallback((sorting: SortingState) => {
		setSorting(sorting)
	}, [])

	const table = useReactTable<T>({
		data,
		columns,
		enableExpanding: true,
		filterFromLeafRows: true,
		state: {
			expanded,
			globalFilter: textFilter,
			columnVisibility,
			rowSelection,
			columnOrder,
			sorting,
		},
		getSubRows(row) {
			const subRows = row.subRows
			return subRows == null
				? []
				: subRows.map(r => ({ ...r, parent: row }))
		},
		onExpandedChange: setExpanded,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		debugTable: false,
		columnResizeMode: 'onChange',
		enableRowSelection: true,
		onRowSelectionChange: setRowSelection,
		onColumnVisibilityChange: setColumnVisibility,
	})

	useEffect(() => {
		const columnSizing =
			JSON.parse(localStorage.getItem('columnSizing') ?? '{}') || {}

		if (columnSizing[LSKey]) {
			table.setColumnSizing(columnSizing[LSKey])
		}
	}, [])

	//Sync row selection for export in test assets table if export context exists
	useEffect(() => {
		if (setSelectedRows) {
			setSelectedRows(
				table.getSelectedRowModel()
					.flatRows as unknown as Row<AssetTableEntryDTO>[]
			)
		}
	}, [table.getSelectedRowModel().flatRows, setSelectedRows])

	const { rows } = table.getRowModel()

	// When a PDF export is in progress, render all rows instead of virtualising.
	const [pdfExportMode, setPdfExportMode] = useState(false)

	useEffect(() => {
		const scrollEl = containerRef?.current ?? tableParentRef.current
		if (!scrollEl) return

		const onStart = (e: Event) => {
			const target = (e as CustomEvent<HTMLElement>).detail
			if (target && target.contains(scrollEl)) {
				setPdfExportMode(true)
			}
		}
		const onEnd = () => setPdfExportMode(false)

		document.addEventListener('pdf-export-start', onStart)
		document.addEventListener('pdf-export-end', onEnd)
		return () => {
			document.removeEventListener('pdf-export-start', onStart)
			document.removeEventListener('pdf-export-end', onEnd)
		}
	}, [containerRef])

	// Optimize virtualization with useCallback to prevent recreating on every render
	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		estimateSize: useCallback(() => 30, []),
		getScrollElement: useCallback(
			() => containerRef?.current ?? tableParentRef.current,
			[containerRef]
		),
		measureElement:
			typeof window !== 'undefined' &&
			navigator.userAgent.indexOf('Firefox') === -1
				? element => element?.getBoundingClientRect().height
				: undefined,
		overscan: pdfExportMode ? rows.length : 30,
	})

	const virtualRows = rowVirtualizer.getVirtualItems()

	const { columnSizing } = table.getState()

	// Optimize by batching localStorage writes
	useEffect(() => {
		if (columnSizing && Object.keys(columnSizing).length > 0) {
			const saveColumnSizing = () => {
				const columnSizingLS =
					JSON.parse(localStorage.getItem('columnSizing') ?? '{}') ||
					{}

				localStorage.setItem(
					'columnSizing',
					JSON.stringify({
						...columnSizingLS,
						[LSKey]: columnSizing,
					})
				)
			}

			// Debounce to avoid rapid successive writes
			const timeoutId = setTimeout(saveColumnSizing, 200)
			return () => clearTimeout(timeoutId)
		}
	}, [columnSizing, LSKey])

	// reorder columns after drag & drop
	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			if (active && over && active.id !== over.id) {
				const oldIndex = columnOrder.indexOf(active.id as string)
				const newIndex = columnOrder.indexOf(over.id as string)
				const newColumnOrder = arrayMove(
					columnOrder,
					oldIndex,
					newIndex
				) satisfies string[]
				if (setColumnOrder) {
					setColumnOrder(newColumnOrder)
				} else {
					setInternalColumnOrder(newColumnOrder)
				}
			}
		},
		[setColumnOrder, columnOrder, setInternalColumnOrder]
	)

	const sensors = useSensors(
		useSensor(MouseSensor, {}),
		useSensor(TouchSensor, {}),
		useSensor(KeyboardSensor, {})
	)

	if (isLoading) return <AstaLoader variant="linear" />
	if (!isLoading && !isFetching && data.length === 0)
		return <EmptyTableView />

	return (
		<DndContext
			key={LSKey}
			collisionDetection={closestCenter}
			modifiers={[restrictToHorizontalAxis]}
			onDragEnd={handleDragEnd}
			sensors={sensors}
		>
			<div ref={tableParentRef} className="w-fit pr-[1px]">
				{/* Even though we're still using semantic table tags, we must use CSS grid and flexbox for dynamic row heights */}
				<table
					key={LSKey}
					className="grid w-full"
					style={{
						width: !isNaN(table.getCenterTotalSize())
							? table.getCenterTotalSize()
							: 'auto',
					}}
				>
					<TableHeaders
						table={table}
						columnOrder={columnOrder}
						sort={sort}
						setSort={setSort}
						setColSorting={setColSorting}
					/>
					<TB
						table={table}
						rows={virtualRows}
						rowOnClick={rowOnClick}
						setRowSelection={setRowSelection}
						contextMenuOptions={contextMenuOptions}
						height={rowVirtualizer.getTotalSize()}
						rowVirtualizer={rowVirtualizer}
						selectedRowId={selectedRowId}
					/>
				</table>
			</div>
		</DndContext>
	)
}
